"use client";

import { ChevronDown, ChevronUp } from "lucide-react";
import Image from "next/image";
import { useState } from "react";
import { StructuredText as StructuredTextDato } from "react-datocms";

import { CtaButton } from "~/components/modules/CtaButton";
import { DownloadSectionFile } from "~/components/modules/DownloadSectionFile";
import { ExternalEmbed } from "~/components/modules/ExternalEmbed";
import { GalleryGrid } from "~/components/modules/GalleryGrid";
import { LpCardBlock } from "~/components/modules/LpCardBlock";
import { PublicationTeaser } from "~/components/modules/PublicationTeaser";
import { ResponsiveBanner } from "~/components/modules/ResponsiveBanner";
import { ResponsiveImage } from "~/components/modules/ResponsiveImage";
// Import existing components for content blocks
import { SingleImage } from "~/components/modules/SingleImage";
import { SpeakerBlock } from "~/components/modules/SpeakerBlock";
import { VideoList } from "~/components/modules/VideoListBlock";
import { Button } from "~/components/ui/button";
import { type FragmentOf, type SiteLocale, readFragment } from "~/lib/graphql";
import { cn } from "~/lib/utils";

import * as fragment from "./graphql";

export function LpAccordion(props: {
  record: FragmentOf<typeof fragment.LpAccordionFragment>;
  locale: SiteLocale;
}) {
  const record = readFragment(fragment.LpAccordionFragment, props.record);
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());

  if (!record) {
    return null;
  }

  const toggleItem = (itemId: string) => {
    setOpenItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  return (
    <div className="container mx-auto mb-10 max-w-[1000px]">
      <div className="relative space-y-4">
        {record.lpAccordionItems.map((item) => {
          const isOpen = openItems.has(item.id);

          return (
            <div
              key={item.id}
              className={`border-border mb-0 border-b py-2 group-data-[secondary]:mb-4 group-data-[secondary]:rounded-[8px] group-data-[secondary]:border group-data-[secondary]:px-6`}
            >
              <Button
                variant="ghost"
                className={cn(
                  "h-auto w-full justify-between rounded-none px-0 py-2 text-left opacity-100 hover:bg-transparent hover:bg-none",
                  item.stickyTitle && isOpen && "sticky top-0 left-0 z-20 w-full border-b",
                )}
                onClick={() => toggleItem(item.id)}
              >
                <div className="flex items-center gap-3">
                  {item.icon && (
                    <div className="flex-shrink-0">
                      <Image
                        src={item.icon.url}
                        alt={item.icon.alt || ""}
                        width={24}
                        height={24}
                        className="object-contain"
                      />
                    </div>
                  )}
                  <h3
                    className="text-[17px] font-semibold whitespace-normal"
                    id={item?.slug ? item.slug : "myid"}
                  >
                    {item.title}
                  </h3>
                </div>
                {isOpen ? (
                  <ChevronUp className="text-muted-foreground h-5 w-5" />
                ) : (
                  <ChevronDown className="text-muted-foreground h-5 w-5" />
                )}
              </Button>

              {isOpen && (
                <div className="py-4">
                  {item.content && <StructuredTextDato data={item.content} />}
                  <>
                    {item.modularContent &&
                      Array.isArray(item.modularContent) &&
                      item.modularContent.length > 0 && (
                        <div className="space-y-4">
                          {item.modularContent.map((block) => {
                            switch (block.__typename) {
                              case "ImageSingleRecord":
                                return <SingleImage key={block.id} record={block} />;
                              case "ResponsiveImageRecord":
                                return (
                                  <ResponsiveImage
                                    key={block.id}
                                    record={block}
                                    locale={props.locale}
                                  />
                                );
                              case "ExternalEmbedRecord":
                                return <ExternalEmbed key={block.id} record={block} />;
                              case "SpeakerblockRecord":
                                return (
                                  <SpeakerBlock
                                    key={block.id}
                                    record={block}
                                    locale={props.locale}
                                  />
                                );
                              case "VideoListRecord":
                                return <VideoList key={block.id} record={block} />;
                              case "PublicationTeaserRecord":
                                return <PublicationTeaser key={block.id} record={block} />;
                              case "GalleryGridRecord":
                                return (
                                  <GalleryGrid
                                    key={block.id}
                                    record={block}
                                    locale={props.locale}
                                  />
                                );
                              case "DownloadSectionFileRecord":
                                return (
                                  <DownloadSectionFile
                                    key={block.id}
                                    record={block}
                                    locale={props.locale}
                                  />
                                );
                              case "ResponsiveBannerRecord":
                                return (
                                  <ResponsiveBanner
                                    key={block.id}
                                    record={block}
                                    locale={props.locale}
                                  />
                                );
                              case "LpCardBlockRecord":
                                return (
                                  <LpCardBlock
                                    key={block.id}
                                    record={block}
                                    locale={props.locale}
                                  />
                                );
                              case "CtaButtonRecord":
                                return (
                                  <CtaButton
                                    key={block.id}
                                    record={block}
                                    locale={props.locale}
                                  />
                                );
                              default:
                                console.warn(
                                  `Unknown block type: ${(block as any).__typename}`,
                                );
                                return null;
                            }
                          })}
                        </div>
                      )}
                  </>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
